/**
 * Pure capture, redaction, and picker helpers for the Admin issue report widget.
 *
 * @remarks
 * The React widget owns UI state, mutation calls, and submission side effects.
 * This module owns the reusable capture boundary that decides which route,
 * element, diagnostics, and picker geometry details can safely leave the
 * browser. Keep privacy-sensitive changes covered by helper tests as well as
 * the end-to-end widget tests.
 *
 * @see docs/features/BUG_TRACKER.md — shared issue-report privacy posture
 * @see docs/LOGGING.md — never persist credentials or PII in diagnostics payloads
 * @see apps/admin/src/components/assistant/__tests__/adminIssueReportCapture.test.ts
 * @see apps/admin/src/components/assistant/__tests__/AdminIssueReportWidget.test.tsx
 */
import type { JsonObject } from "@/types/adminV2Json";
import type { FrontendDiagnosticsSnapshot } from "@/utils/logging/frontendTelemetrySink";

export interface ElementSelection {
  selector: string;
  label: string;
  tagName: string;
  role: string | null;
  display: string;
  selectedHtml: string | null;
  visibleText: string | null;
  nearbyTextContext: string[];
  headings: string[];
  actionLabels: string[];
  statusText: string[];
  parentContext: {
    label: string | null;
    tagName: string;
    role: string | null;
    display: string;
    selectedHtml: string | null;
    visibleText: string | null;
    nearbyTextContext: string[];
    headings: string[];
    actionLabels: string[];
    statusText: string[];
  } | null;
  rect: {
    top: number;
    left: number;
    width: number;
    height: number;
    right: number;
    bottom: number;
  };
}

export interface DiagnosticsDigestRecord {
  level: "warn" | "error" | "fatal";
  message: string;
  route: string | null;
}

export interface DiagnosticsDigestFailedRequest {
  method: string | null;
  statusCode: number | null;
  path: string | null;
}

export interface DiagnosticsDigest {
  errorCount: number;
  warningCount: number;
  /** Total qualifying recent failed-request records; `failedRequests` is display-capped. */
  failedRequestCount: number;
  notableEvents: DiagnosticsDigestRecord[];
  failedRequests: DiagnosticsDigestFailedRequest[];
}

export interface PickerSelectionAdvisory {
  code: "too_small" | "too_broad";
  message: string;
}

export interface PickerConfirmationCardPosition {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
}

const PICKER_CONFIRMATION_CARD_WIDTH = 360;
const PICKER_CONFIRMATION_CARD_MIN_HEIGHT = 252;
const PICKER_CONFIRMATION_MARGIN = 12;
const PICKER_CONFIRMATION_GAP = 16;
const MAX_SELECTED_HTML_SNAPSHOT_CHARS = 1_800;
const MAX_SELECTED_HTML_CHILDREN = 6;
const MAX_SELECTED_HTML_DEPTH = 3;
const MAX_NEARBY_TEXT_CONTEXT_ITEMS = 6;
const MAX_NEARBY_TEXT_CONTEXT_LENGTH = 180;
const MAX_VISIBLE_TEXT_LENGTH = 320;
const MAX_CONTEXT_SIGNAL_ITEMS = 4;
const MAX_CONTEXT_SIGNAL_LENGTH = 160;
const EMAIL_REDACTION_PATTERN = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const UUID_REDACTION_PATTERN =
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi;
const LONG_NUMBER_REDACTION_PATTERN = /\b\d{4,}\b/g;
const OPAQUE_TOKEN_REDACTION_PATTERN = /\b[A-Z0-9_-]{16,}\b/gi;
const PICKER_MIN_DIMENSION_PX = 24;
const ISSUE_REPORT_ALLOWED_ROUTE_QUERY_KEYS = new Set(["tab"]);
const ISSUE_REPORT_ALLOWED_ROUTE_VALUE_PATTERN = /^[a-z0-9_-]{1,40}$/i;
const ISSUE_REPORT_DEBUG_CAPTURE_STORAGE_KEY = "hc-issue-report-debug-capture";
const ISSUE_REPORT_REDACTED_PATH_SEGMENT = ":redacted";
const ISSUE_REPORT_UUID_SEGMENT_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const ISSUE_REPORT_LONG_NUMERIC_SEGMENT_PATTERN = /^\d{4,}$/;
const ISSUE_REPORT_OPAQUE_SEGMENT_PATTERN = /^[a-z0-9_-]{16,}$/i;

const PICKER_NON_SELECTABLE_TAGS = new Set([
  "html",
  "body",
  "p",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
]);
const PICKER_SELECTABLE_DISPLAY_VALUES = new Set([
  "block",
  "flex",
  "grid",
  "table",
  "list-item",
  "flow-root",
]);

export const buildAllowedIssueReportSearch = (rawSearch: string): string => {
  const search = rawSearch.startsWith("?") ? rawSearch.slice(1) : rawSearch;
  if (!search) {
    return "";
  }

  const params = new URLSearchParams(search);
  const allowed = new URLSearchParams();

  for (const [key, value] of params.entries()) {
    if (!ISSUE_REPORT_ALLOWED_ROUTE_QUERY_KEYS.has(key)) {
      continue;
    }

    const trimmedValue = value.trim();
    if (
      !trimmedValue ||
      !ISSUE_REPORT_ALLOWED_ROUTE_VALUE_PATTERN.test(trimmedValue)
    ) {
      continue;
    }

    if (!allowed.has(key)) {
      allowed.append(key, trimmedValue.slice(0, 40));
    }
  }

  const serialized = allowed.toString();
  return serialized ? `?${serialized}` : "";
};

const decodeIssueReportPathSegment = (segment: string): string => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

const shouldRedactIssueReportPathSegment = (segment: string): boolean => {
  if (!segment) {
    return false;
  }

  const decodedSegment = decodeIssueReportPathSegment(segment);
  if (
    decodedSegment.length > 40 ||
    /@/.test(decodedSegment) ||
    /%[0-9a-f]{2}/i.test(segment) ||
    ISSUE_REPORT_UUID_SEGMENT_PATTERN.test(decodedSegment) ||
    ISSUE_REPORT_LONG_NUMERIC_SEGMENT_PATTERN.test(decodedSegment)
  ) {
    return true;
  }

  return (
    decodedSegment.length >= 16 &&
    ISSUE_REPORT_OPAQUE_SEGMENT_PATTERN.test(decodedSegment)
  );
};

/**
 * Redacts path segments that look like IDs, emails, or opaque tokens before
 * issue-report route hints leave the browser.
 *
 * @see docs/features/BUG_TRACKER.md — shared issue-report privacy posture
 * @see docs/LOGGING.md — never persist credentials or PII in diagnostics payloads
 */
export const buildIssueReportPathname = (pathname: string): string => {
  if (!pathname || pathname === "/") {
    return pathname || "/";
  }

  const sanitizedPath = pathname
    .split("/")
    .map((segment, index) =>
      index === 0 || !shouldRedactIssueReportPathSegment(segment)
        ? segment
        : ISSUE_REPORT_REDACTED_PATH_SEGMENT,
    )
    .join("/");

  return sanitizedPath || "/";
};

/**
 * Builds the route state that can safely enter the shared issue-report queue.
 *
 * @remarks
 * Query keys default-deny here because support issue reports share the same
 * privacy posture as Bug Tracker URLs: tokens, spoof links, and arbitrary
 * query values are not safe to persist.
 *
 * @see docs/features/BUG_TRACKER.md — shared bug-report privacy rules
 * @see docs/LOGGING.md — never persist credentials or PII in diagnostics payloads
 */
export const buildIssueReportRouteHint = (
  pathname: string,
  search: string,
): string =>
  `${buildIssueReportPathname(pathname)}${buildAllowedIssueReportSearch(search)}`;

export const buildIssueReportUrl = (
  pathname: string,
  search: string,
): string => {
  const routeHint = buildIssueReportRouteHint(pathname, search);
  if (typeof window === "undefined") {
    return routeHint;
  }

  return `${window.location.origin}${routeHint}`;
};

export const isIssueReportDebugCaptureEnabled = (): boolean => {
  if (!import.meta.env.DEV || typeof window === "undefined") {
    return false;
  }

  try {
    return (
      window.localStorage.getItem(ISSUE_REPORT_DEBUG_CAPTURE_STORAGE_KEY) ===
      "enabled"
    );
  } catch {
    return false;
  }
};

export const buildIssueReportElementLabel = (
  selection: Pick<ElementSelection, "label" | "selector" | "tagName" | "role">,
  debugCaptureEnabled: boolean,
): string => {
  if (debugCaptureEnabled) {
    return selection.label;
  }

  const dataTestIdMatch = /^\[data-testid="([^"]+)"\]$/.exec(
    selection.selector,
  );
  if (dataTestIdMatch) {
    return `data-testid=${dataTestIdMatch[1]}`;
  }

  return selection.role
    ? `${selection.tagName}[role=${selection.role}]`
    : selection.tagName;
};

const buildIssueReportParentContext = (
  parentContext: ElementSelection["parentContext"],
  debugCaptureEnabled: boolean,
): JsonObject | null => {
  if (!parentContext) {
    return null;
  }

  return {
    tagName: parentContext.tagName,
    role: parentContext.role,
    display: parentContext.display,
    ...(debugCaptureEnabled
      ? {
          label: parentContext.label,
          selectedHtml: parentContext.selectedHtml,
          visibleText: parentContext.visibleText,
          nearbyTextContext: parentContext.nearbyTextContext,
          headings: parentContext.headings,
          actionLabels: parentContext.actionLabels,
          statusText: parentContext.statusText,
        }
      : {}),
  };
};

/**
 * Captures the allowlisted picker evidence that shared issue reports attach by
 * default.
 *
 * @remarks
 * Rich HTML and nearby-text snapshots stay behind the local-dev-only debug
 * escape hatch so operators still get useful structure and visible labels
 * without persisting the broader DOM snapshot by default.
 *
 * @see docs/features/BUG_TRACKER.md — shared bug-report privacy rules
 * @see docs/LOGGING.md — never persist credentials or PII in diagnostics payloads
 */
export const buildIssueReportSelectionContext = (
  selection: ElementSelection | null,
): JsonObject | null => {
  if (!selection) {
    return null;
  }

  const debugCaptureEnabled = isIssueReportDebugCaptureEnabled();

  return {
    label: buildIssueReportElementLabel(selection, debugCaptureEnabled),
    tagName: selection.tagName,
    role: selection.role,
    display: selection.display,
    visibleText: selection.visibleText,
    headings: selection.headings,
    actionLabels: selection.actionLabels,
    statusText: selection.statusText,
    parentContext: buildIssueReportParentContext(
      selection.parentContext,
      debugCaptureEnabled,
    ),
    ...(debugCaptureEnabled
      ? {
          selector: selection.selector,
          selectedHtml: selection.selectedHtml,
          nearbyTextContext: selection.nearbyTextContext,
        }
      : {}),
  };
};

export const buildIssueReportSuggestionElement = (
  selection: ElementSelection,
): JsonObject => {
  const debugCaptureEnabled = isIssueReportDebugCaptureEnabled();

  return {
    label: buildIssueReportElementLabel(selection, debugCaptureEnabled),
    rect: selection.rect,
    tagName: selection.tagName,
    role: selection.role,
    display: selection.display,
    visibleText: selection.visibleText,
    headings: selection.headings,
    actionLabels: selection.actionLabels,
    statusText: selection.statusText,
    parentContext: buildIssueReportParentContext(
      selection.parentContext,
      debugCaptureEnabled,
    ),
    ...(debugCaptureEnabled
      ? {
          selector: selection.selector,
          selectedHtml: selection.selectedHtml,
          nearbyTextContext: selection.nearbyTextContext,
        }
      : {}),
  };
};

export const normalizeText = (value: string, maxChars: number): string => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  return trimmed.slice(0, maxChars);
};

const clampNumber = (value: number, min: number, max: number): number => {
  if (max <= min) {
    return min;
  }

  return Math.min(Math.max(value, min), max);
};

const getElementLabel = (element: Element): string => {
  const ariaLabel = element.getAttribute("aria-label")?.trim();
  if (ariaLabel) return ariaLabel.slice(0, 180);

  const testId = element.getAttribute("data-testid")?.trim();
  if (testId) return `data-testid=${testId}`.slice(0, 180);

  const textContent = element.textContent.trim();
  if (textContent) {
    return textContent.replace(/\s+/g, " ").slice(0, 180);
  }

  return element.tagName.toLowerCase();
};

const buildCssSelector = (element: Element): string => {
  const id = element.getAttribute("id")?.trim();
  if (id) {
    return `#${id}`;
  }

  const testId = element.getAttribute("data-testid")?.trim();
  if (testId) {
    return `[data-testid="${testId}"]`;
  }

  const segments: string[] = [];
  let current: Element = element;
  let parent = current.parentElement;
  if (parent === null) {
    return current.tagName.toLowerCase();
  }

  while (segments.length < 5) {
    const tagName = current.tagName.toLowerCase();
    const currentTagName = current.tagName;
    const siblings = Array.from(parent.children).filter(
      (sibling: Element) => sibling.tagName === currentTagName,
    );
    const siblingIndex = siblings.indexOf(current) + 1;
    const suffix = siblings.length > 1 ? `:nth-of-type(${siblingIndex})` : "";
    segments.unshift(`${tagName}${suffix}`);

    current = parent;
    parent = current.parentElement;
    if (parent === null) {
      break;
    }
  }

  return segments.join(" > ");
};

/**
 * Removes URL query and fragment details before picker text snapshots enter
 * shared support and bug-report queues.
 *
 * @see docs/features/BUG_TRACKER.md — shared issue-report privacy posture
 * @see docs/LOGGING.md — never persist credentials or PII in diagnostics payloads
 */
const stripSensitiveUrlDetails = (value: string): string =>
  value
    .replace(/https?:\/\/[^\s)]+/gi, (match) => {
      try {
        new URL(match);
        return "[redacted-url]";
      } catch {
        return "[redacted-url]";
      }
    })
    .replace(/(\/[^\s?#)]*)[?#][^\s)]+/g, "$1")
    .replace(EMAIL_REDACTION_PATTERN, "[redacted-email]")
    .replace(UUID_REDACTION_PATTERN, "[redacted-id]")
    .replace(LONG_NUMBER_REDACTION_PATTERN, "[redacted-number]")
    .replace(OPAQUE_TOKEN_REDACTION_PATTERN, "[redacted-token]");

const sanitizeInlineText = (value: string, maxChars: number): string => {
  const collapsed = stripSensitiveUrlDetails(value.replace(/\s+/g, " ").trim());
  if (!collapsed) {
    return "";
  }

  return collapsed.length > maxChars
    ? `${collapsed.slice(0, maxChars)}…`
    : collapsed;
};

const collectUniqueSanitizedText = (
  values: Array<string | null | undefined>,
  maxItems: number,
  maxChars: number,
): string[] => {
  const unique: string[] = [];

  for (const value of values) {
    if (!value) {
      continue;
    }

    const sanitized = sanitizeInlineText(value, maxChars);
    if (!sanitized || unique.includes(sanitized)) {
      continue;
    }

    unique.push(sanitized);
    if (unique.length >= maxItems) {
      break;
    }
  }

  return unique;
};

const buildVisibleTextSummary = (element: Element): string | null => {
  const summary = sanitizeInlineText(
    element.textContent,
    MAX_VISIBLE_TEXT_LENGTH,
  );
  return summary || null;
};

const buildHeadingContext = (element: Element): string[] =>
  collectUniqueSanitizedText(
    [
      element
        .closest("section, article, main, [role='region']")
        ?.querySelector("h1, h2, h3, h4, h5, h6, [role='heading']")
        ?.textContent ?? "",
      ...Array.from(
        element.querySelectorAll("h1, h2, h3, h4, h5, h6, [role='heading']"),
      ).map((node) => node.textContent),
    ],
    MAX_CONTEXT_SIGNAL_ITEMS,
    MAX_CONTEXT_SIGNAL_LENGTH,
  );

const buildActionLabels = (element: Element): string[] =>
  collectUniqueSanitizedText(
    Array.from(
      element.querySelectorAll(
        "button, a, [role='button'], [role='link'], input[type='submit'], input[type='button']",
      ),
    ).map((node) => {
      if (!(node instanceof HTMLElement)) {
        return "";
      }

      const ariaLabel = node.getAttribute("aria-label");
      if (ariaLabel !== null) {
        return ariaLabel;
      }

      const textContent = node.textContent.trim();
      if (textContent.length > 0) {
        return textContent;
      }

      return node.getAttribute("value") ?? "";
    }),
    MAX_CONTEXT_SIGNAL_ITEMS,
    MAX_CONTEXT_SIGNAL_LENGTH,
  );

const buildStatusText = (element: Element): string[] =>
  collectUniqueSanitizedText(
    [
      (element instanceof HTMLElement
        ? element.getAttribute("aria-label")
        : null) ?? "",
      ...Array.from(
        element.querySelectorAll(
          "[role='alert'], [role='status'], [aria-live], [aria-invalid='true'], [data-status], [data-severity]",
        ),
      ).map((node) =>
        node instanceof HTMLElement
          ? (() => {
              const ariaLabel = node.getAttribute("aria-label");
              if (ariaLabel !== null) {
                return ariaLabel;
              }

              return node.textContent || node.getAttribute("value");
            })()
          : node.textContent,
      ),
    ],
    MAX_CONTEXT_SIGNAL_ITEMS,
    MAX_CONTEXT_SIGNAL_LENGTH,
  );

const buildElementContextSummary = (element: HTMLElement) => ({
  label: getElementLabel(element),
  tagName: element.tagName.toLowerCase(),
  role: element.getAttribute("role"),
  display: window.getComputedStyle(element).display,
  selectedHtml: buildSelectedElementHtmlSnapshot(element),
  visibleText: buildVisibleTextSummary(element),
  nearbyTextContext: buildNearbyTextContext(element),
  headings: buildHeadingContext(element),
  actionLabels: buildActionLabels(element),
  statusText: buildStatusText(element),
});

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const serializeSelectedElementHtml = (
  element: Element,
  remainingCharsRef: { value: number },
  depth = 0,
): string => {
  if (remainingCharsRef.value <= 0 || depth > MAX_SELECTED_HTML_DEPTH) {
    return "";
  }

  const tagName = element.tagName.toLowerCase();
  if (["script", "style", "noscript", "template"].includes(tagName)) {
    return "";
  }

  const attributeEntries: Array<[string, string]> = [];
  const role = element.getAttribute("role")?.trim();
  const ariaLabel = element.getAttribute("aria-label")?.trim();
  const testId = element.getAttribute("data-testid")?.trim();
  const type = element.getAttribute("type")?.trim();
  const ariaExpanded = element.getAttribute("aria-expanded")?.trim();
  const ariaSelected = element.getAttribute("aria-selected")?.trim();

  if (role) attributeEntries.push(["role", sanitizeInlineText(role, 80)]);
  if (ariaLabel) {
    attributeEntries.push(["aria-label", sanitizeInlineText(ariaLabel, 120)]);
  }
  if (testId) {
    attributeEntries.push(["data-testid", sanitizeInlineText(testId, 80)]);
  }
  if (type) attributeEntries.push(["type", sanitizeInlineText(type, 40)]);
  if (ariaExpanded) {
    attributeEntries.push([
      "aria-expanded",
      sanitizeInlineText(ariaExpanded, 12),
    ]);
  }
  if (ariaSelected) {
    attributeEntries.push([
      "aria-selected",
      sanitizeInlineText(ariaSelected, 12),
    ]);
  }

  const attributeString = attributeEntries
    .map(([name, value]) => `${name}="${escapeHtml(value)}"`)
    .join(" ");
  let html = `<${tagName}${attributeString ? ` ${attributeString}` : ""}>`;
  remainingCharsRef.value -= html.length;

  const childNodes = Array.from(element.childNodes).slice(
    0,
    MAX_SELECTED_HTML_CHILDREN,
  );
  for (const childNode of childNodes) {
    if (remainingCharsRef.value <= 0) {
      break;
    }

    if (childNode.nodeType === Node.TEXT_NODE) {
      const text = sanitizeInlineText(
        childNode.textContent ?? "",
        Math.min(remainingCharsRef.value, 120),
      );
      if (!text) {
        continue;
      }

      const escaped = escapeHtml(text);
      html += escaped;
      remainingCharsRef.value -= escaped.length;
      continue;
    }

    if (childNode instanceof Element) {
      html += serializeSelectedElementHtml(
        childNode,
        remainingCharsRef,
        depth + 1,
      );
    }
  }

  if (element.childNodes.length > MAX_SELECTED_HTML_CHILDREN) {
    html += "…";
    remainingCharsRef.value -= 1;
  }

  html += `</${tagName}>`;
  remainingCharsRef.value -= tagName.length + 3;
  return html;
};

const buildSelectedElementHtmlSnapshot = (element: Element): string | null => {
  const remainingCharsRef = { value: MAX_SELECTED_HTML_SNAPSHOT_CHARS };
  const html = serializeSelectedElementHtml(element, remainingCharsRef);
  const trimmed = html.trim();
  return trimmed ? trimmed : null;
};

const buildNearbyTextContext = (element: Element): string[] => {
  const candidates = [
    element
      .closest("section, article, main, [role='region']")
      ?.querySelector("h1, h2, h3, h4, h5, h6")?.textContent ?? "",
    element.closest("label")?.textContent ?? "",
    element.parentElement?.textContent ?? "",
    element.closest("[aria-label]")?.getAttribute("aria-label") ?? "",
  ];

  const unique: string[] = [];
  for (const candidate of candidates) {
    const sanitized = sanitizeInlineText(
      candidate,
      MAX_NEARBY_TEXT_CONTEXT_LENGTH,
    );
    if (!sanitized || unique.includes(sanitized)) {
      continue;
    }

    unique.push(sanitized);
    if (unique.length >= MAX_NEARBY_TEXT_CONTEXT_ITEMS) {
      break;
    }
  }

  return unique;
};

export const buildDiagnosticsDigest = (
  snapshot: FrontendDiagnosticsSnapshot,
): DiagnosticsDigest => {
  const relevantRecords = snapshot.records
    .filter((record) => ["warn", "error", "fatal"].includes(record.level))
    .slice(-6)
    .reverse();

  const notableEvents: DiagnosticsDigestRecord[] = [];
  const failedRequests: DiagnosticsDigestFailedRequest[] = [];
  let failedRequestCount = 0;

  for (const record of relevantRecords) {
    if (notableEvents.length < 3) {
      notableEvents.push({
        level: record.level as DiagnosticsDigestRecord["level"],
        message: sanitizeInlineText(record.message, 180),
        route: record.route,
      });
    }

    const statusCode =
      typeof record.attributes.statusCode === "number"
        ? record.attributes.statusCode
        : typeof record.attributes.httpStatus === "number"
          ? record.attributes.httpStatus
          : null;
    const method =
      typeof record.attributes.method === "string"
        ? sanitizeInlineText(record.attributes.method.toUpperCase(), 12)
        : null;
    const path =
      typeof record.attributes.route === "string"
        ? sanitizeInlineText(record.attributes.route, 180)
        : record.route;

    const hasFailedRequestSignal =
      statusCode !== null || method !== null || path !== null;
    if (hasFailedRequestSignal) {
      failedRequestCount += 1;
    }

    if (failedRequests.length < 3 && hasFailedRequestSignal) {
      failedRequests.push({
        method,
        statusCode,
        path,
      });
    }
  }

  const errorCount = snapshot.records.filter(
    (record) => record.level === "error",
  ).length;
  const warningCount = snapshot.records.filter(
    (record) => record.level === "warn",
  ).length;

  return {
    errorCount,
    warningCount,
    failedRequestCount,
    notableEvents,
    failedRequests,
  };
};

export const isSelectableIssueElement = (
  element: Element,
): element is HTMLElement => {
  if (!(element instanceof HTMLElement)) {
    return false;
  }

  if (element.closest("[data-hc-admin-issue-widget-root]")) {
    return false;
  }

  if (element.id === "root") {
    return false;
  }

  const tagName = element.tagName.toLowerCase();
  if (PICKER_NON_SELECTABLE_TAGS.has(tagName)) {
    return false;
  }

  const style = window.getComputedStyle(element);
  if (!PICKER_SELECTABLE_DISPLAY_VALUES.has(style.display)) {
    return false;
  }

  if (style.visibility === "hidden") {
    return false;
  }

  const rect = element.getBoundingClientRect();
  return (
    rect.width >= PICKER_MIN_DIMENSION_PX &&
    rect.height >= PICKER_MIN_DIMENSION_PX
  );
};

export const resolveSelectableIssueElement = (
  start: Element,
): HTMLElement | null => {
  let current: Element | null = start;
  let depth = 0;

  while (current && depth < 8) {
    if (isSelectableIssueElement(current)) {
      return current;
    }

    current = current.parentElement;
    depth += 1;
  }

  return null;
};

export const resolveBroaderSelectableIssueElement = (
  start: HTMLElement,
  viewportWidth: number,
  viewportHeight: number,
): HTMLElement | null => {
  const startRect = start.getBoundingClientRect();
  const startArea = Math.max(startRect.width * startRect.height, 1);
  const viewportArea = Math.max(viewportWidth * viewportHeight, 1);
  let current: Element | null = start.parentElement;
  let depth = 0;
  let fallbackCandidate: HTMLElement | null = null;

  while (current && depth < 8) {
    if (isSelectableIssueElement(current)) {
      const rect = current.getBoundingClientRect();
      const candidateArea = Math.max(rect.width * rect.height, 1);
      const widthRatio = rect.width / Math.max(viewportWidth, 1);
      const heightRatio = rect.height / Math.max(viewportHeight, 1);
      const areaRatio = candidateArea / viewportArea;
      const isMeaningfullyBroader =
        candidateArea >= startArea * 1.35 ||
        rect.width >= startRect.width + 80 ||
        rect.height >= startRect.height + 80;
      const looksTooBroad =
        widthRatio > 0.88 || heightRatio > 0.72 || areaRatio > 0.38;
      const looksNearlyFullPage =
        widthRatio > 0.96 || heightRatio > 0.86 || areaRatio > 0.7;

      if (isMeaningfullyBroader && !fallbackCandidate && !looksNearlyFullPage) {
        fallbackCandidate = current;
      }

      if (isMeaningfullyBroader && !looksTooBroad) {
        return current;
      }
    }

    current = current.parentElement;
    depth += 1;
  }

  return fallbackCandidate;
};

/**
 * Builds the sanitized DOM evidence attached to an element-selection report.
 *
 * @remarks
 * The selected element is sanitized here before later payload helpers decide
 * which fields can cross the normal or local-debug boundary.
 *
 * @see {@link buildIssueReportSelectionContext}
 * @see {@link buildIssueReportSuggestionElement}
 */
export const buildElementSelection = (
  element: HTMLElement,
): ElementSelection => {
  const rect = element.getBoundingClientRect();
  const broaderContextElement = resolveBroaderSelectableIssueElement(
    element,
    typeof window === "undefined" ? rect.width : window.innerWidth,
    typeof window === "undefined" ? rect.height : window.innerHeight,
  );
  const elementContext = buildElementContextSummary(element);

  return {
    selector: buildCssSelector(element),
    ...elementContext,
    rect: {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
      right: rect.right,
      bottom: rect.bottom,
    },
    parentContext:
      broaderContextElement && broaderContextElement !== element
        ? buildElementContextSummary(broaderContextElement)
        : null,
  };
};

export const buildPickerSelectionAdvisories = (
  rect: ElementSelection["rect"],
  viewportWidth: number,
  viewportHeight: number,
): PickerSelectionAdvisory[] => {
  const viewportArea = Math.max(viewportWidth * viewportHeight, 1);
  const selectionArea = Math.max(rect.width * rect.height, 1);
  const widthRatio = rect.width / Math.max(viewportWidth, 1);
  const heightRatio = rect.height / Math.max(viewportHeight, 1);
  const areaRatio = selectionArea / viewportArea;
  const advisories: PickerSelectionAdvisory[] = [];

  if (rect.width < 220 || rect.height < 96 || areaRatio < 0.025) {
    advisories.push({
      code: "too_small",
      message:
        "This selection looks a bit small, which might be ok. But just ensure the selected area includes where you are seeing the bug.",
    });
  }

  if (widthRatio > 0.88 || heightRatio > 0.72 || areaRatio > 0.38) {
    advisories.push({
      code: "too_broad",
      message:
        "This selection covers a large part of the page. If the issue is limited to one panel or form section, you may want to choose a narrower area.",
    });
  }

  return advisories;
};

export const buildPickerConfirmationCardPosition = (
  rect: ElementSelection["rect"],
  viewportWidth: number,
  viewportHeight: number,
): PickerConfirmationCardPosition => {
  const margin = PICKER_CONFIRMATION_MARGIN;
  const gap = PICKER_CONFIRMATION_GAP;
  const availableWidth = Math.max(viewportWidth - margin * 2, 220);
  const width = Math.min(PICKER_CONFIRMATION_CARD_WIDTH, availableWidth);
  const maxHeight = Math.max(220, viewportHeight - margin * 2);
  const estimatedHeight = Math.min(
    PICKER_CONFIRMATION_CARD_MIN_HEIGHT,
    maxHeight,
  );

  const candidates = [
    {
      overflow: Math.max(
        0,
        width - (viewportWidth - margin - rect.right - gap),
      ),
      top: clampNumber(
        rect.top + rect.height / 2 - estimatedHeight / 2,
        margin,
        viewportHeight - estimatedHeight - margin,
      ),
      left: rect.right + gap,
    },
    {
      overflow: Math.max(0, width - (rect.left - margin - gap)),
      top: clampNumber(
        rect.top + rect.height / 2 - estimatedHeight / 2,
        margin,
        viewportHeight - estimatedHeight - margin,
      ),
      left: rect.left - gap - width,
    },
    {
      overflow: Math.max(
        0,
        estimatedHeight - (viewportHeight - margin - rect.bottom - gap),
      ),
      top: rect.bottom + gap,
      left: clampNumber(
        rect.left + rect.width / 2 - width / 2,
        margin,
        viewportWidth - width - margin,
      ),
    },
    {
      overflow: Math.max(0, estimatedHeight - (rect.top - margin - gap)),
      top: rect.top - gap - estimatedHeight,
      left: clampNumber(
        rect.left + rect.width / 2 - width / 2,
        margin,
        viewportWidth - width - margin,
      ),
    },
  ];

  const bestCandidate = candidates.reduce((best, candidate) =>
    candidate.overflow < best.overflow ? candidate : best,
  );

  return {
    width,
    maxHeight,
    top: clampNumber(
      bestCandidate.top,
      margin,
      viewportHeight - estimatedHeight - margin,
    ),
    left: clampNumber(
      bestCandidate.left,
      margin,
      viewportWidth - width - margin,
    ),
  };
};

export const dataUrlByteSize = (dataUrl: string): number => {
  const base64 = dataUrl.split(",", 2)[1] ?? "";
  if (!base64) return 0;

  const padding = base64.match(/=+$/)?.[0].length ?? 0;
  return (base64.length * 3) / 4 - padding;
};

export const selectImageFileFromClipboard = (
  clipboardData: DataTransfer,
): File | null => {
  const items = Array.from(clipboardData.items);

  for (const item of items) {
    if (item.kind !== "file") continue;
    if (!item.type.toLowerCase().startsWith("image/")) continue;
    const file = item.getAsFile();
    if (file) {
      return file;
    }
  }

  return null;
};
